import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { PropertyUnit } from 'src/app/models/interfaces/property-unit';
import { ColumnsUtilService } from 'src/app/core/services/uitls/columns-util/columns-util.service';
import { FiltersService } from 'src/app/core/services/filters/filters.service';
import { FilterUtilService } from 'src/app/core/services/uitls/filter-utilizer/filter-util.service';
import { PropertyUnitsService } from '../../services/property-units.service';
import { tablesHeaderData } from 'src/app/shared/global/tablesHeaderData';

@Component({
  selector: 'app-property-units',
  templateUrl: './property-units.component.html',
  styleUrls: ['./property-units.component.scss']
})
export class PropertyUnitsComponent implements OnInit {
  propertyUnits: PropertyUnit[] = [];
  searchValue: string = '';
  totalRecords: number = 0;
  page: number = 1;
  page_size: number = 20;
  config: any;
  selectedRowsIndex: any[] = [];
  selectedRowsPK: any[] = [];
  spinner: boolean = true;
  filterPage: string = 'PROPERTY_UNIT';
  pageTitle: string = 'الوحدات العقارية';
  tableHeaderData: any[] = [];
  updatedPropertyUnits = new BehaviorSubject<PropertyUnit[]>([]);
  propertyUnitData = this.updatedPropertyUnits.asObservable();
  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  isTotalRowsSelected: boolean = false;
  currentItemsPerPage: number = 0;
  selectedQuery: any = {};
  filterParamsUrl: Record<string, string | string[]> = {};
  filterData: any;
  filterDataInit: any = {};
  
  @ViewChild('goUp', { static: true }) contentPage!: ElementRef;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private propertyUnitsService: PropertyUnitsService,
    private columnsUtil: ColumnsUtilService,
    private filter: FiltersService,
    private filterUtil: FilterUtilService
  ) {
    this.config = {
      id: 'propertyUnitsPagination',
      itemsPerPage: this.page_size,
      currentPage: this.page,
      totalItems: this.totalRecords,
    };
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.pageTitle);
    this.initializeTableHeaders();
    this.getPropertyUnitsFilter();
    
    this.route.queryParams.subscribe((params) => {
      if (params['page'] && params['page_size']) {
        this.page = Number(params['page']);
        this.page_size = Number(params['page_size']);
        
        // Extract filter parameters from URL
        const { page, page_size, ...restparams } = params;
        this.filterParamsUrl = { ...restparams };
        this.filterDataInit = { ...restparams };
        
        this.getPropertyUnits();
      } else {
        // Set default params without triggering a new subscription
        this.router.navigate([], {
          queryParams: { page: 1, page_size: 20 },
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  initializeTableHeaders(): void {
    this.tableHeaderData = tablesHeaderData.PROPERTY_UNITS_HEADER_DATA;
    this.columnsUtil.applyColumnsData(this.tableHeaderData);
  }

  getPropertyUnits(): void {
    this.spinner = true;
    const params: any = {
      page: this.page,
      page_size: this.page_size,
      ...this.filterParamsUrl
    };

    // Only add query parameter if it has a value
    if (this.searchValue) {
      params.query = this.searchValue;
    }

    this.propertyUnitsService.getPropertyUnits(params).subscribe({
      next: (response) => {
        this.propertyUnits = response.results;
        this.totalRecords = response.count;
        this.currentItemsPerPage = response.results.length;
        
        this.config = {
          id: 'propertyUnitsPagination',
          itemsPerPage: this.page_size,
          currentPage: this.page,
          totalItems: response.count,
        };
        
        this.updatedPropertyUnits.next(this.propertyUnits);
        this.spinner = false;
      },
      error: (error) => {
        console.error('Error fetching property units:', error);
        this.spinner = false;
      }
    });
  }

  quickSearch(searchValue: string): void {
    this.searchValue = searchValue;
    this.page = 1;
    this.config = {
      ...this.config,
      currentPage: 1
    };
    this.router.navigate([], {
      queryParams: { page: 1, query: searchValue || null },
      queryParamsHandling: 'merge',
    });
  }

  pageChanged(event: any): void {
    this.page = event;
    this.config = {
      ...this.config,
      currentPage: event
    };
    this.router.navigate([], {
      queryParams: { page: event },
      queryParamsHandling: 'merge',
    });
    this.contentPage?.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  pageSizeChanged(event: any): void {
    this.page_size = event;
    this.page = 1;
    this.config = {
      ...this.config,
      itemsPerPage: event,
      currentPage: 1
    };
    this.router.navigate([], {
      queryParams: { page: 1, page_size: event },
      queryParamsHandling: 'merge',
    });
  }

  changeToSpecific(event: any): void {
    this.page = event;
    this.config = {
      ...this.config,
      currentPage: event
    };
    this.router.navigate([], {
      queryParams: { page: event },
      queryParamsHandling: 'merge',
    });
  }

  toggleSorting(event: any): void {
    this.spinner = true;
    if (event.col?.length) {
      this.filterParamsUrl['ordering'] = event.col;
    } else {
      delete this.filterParamsUrl['ordering'];
    }
    
    this.router.navigate([], {
      queryParams: { ...this.filterParamsUrl, page: this.page, page_size: this.page_size },
      queryParamsHandling: '',
    });
  }

  getPropertyUnitsFilter(): void {
    const params = {
      filter_page: this.filterPage
    };
   
    this.filter.getFiltersData(params).subscribe({
      next: (data) => {
        this.filterData = data;
      },
      error: (error) => {
        console.error('Error fetching filter data:', error);
      }
    });
  }

  filterFormValues(event: any): void {
    this.filterParamsUrl = event.filterParamsUrl;
    this.spinner = true;
    this.page = 1;
    this.config = {
      ...this.config,
      currentPage: 1
    };
    this.router.navigate([], {
      queryParams: { page: 1, page_size: this.page_size, ...this.filterParamsUrl },
      queryParamsHandling: '',
    });
  }

  getSelectedRow(event: any): void {
    this.selectedRowsPK = event.selectedRowsPK;
    this.selectedRowsIndex = event.selectedRowsIndex;
  }

  selectTotalRows(event: boolean): void {
    this.isTotalRowsSelected = event;
    if (event) {
      this.selectedQuery = {
        search: this.searchValue,
        ...this.filterParamsUrl
      };
    }
  }

  openDetailPage(id: string): void {
    this.router.navigate(['/pages/property-units', id]);
  }

  onOrdersCountClick(row: any): void {
    if (row.id) {
      this.router.navigate(['/pages/orders/orders-list'], {
        queryParams: {
          property_unit: row.id,
          page: 1,
          page_size: 20
        }
      });
    }
  }

}
